import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import Modal from "@/components/modal";
import { api } from "@/helper/api";

export interface AdditionalGroupAssignment {
  group_id: number;
  include_company_descendants: boolean;
}

interface Group {
  id: number;
  name: string;
}

interface Props {
  companyId: string;
  assignments: AdditionalGroupAssignment[];
  onClose: () => void;
  onSaved: () => void;
}

export const toAdditionalGroupAssignments = (
  assignments: AdditionalGroupAssignment[]
) => assignments;

export default function CompanyAdditionalGroupAssignments({
  companyId,
  assignments,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedAssignments, setSelectedAssignments] = useState(assignments);
  const [inheritedAssignments, setInheritedAssignments] = useState<
    AdditionalGroupAssignment[]
  >([]);

  useEffect(() => {
    api.genericApiRequest({
      entity: "group",
      method: "get",
      parametersToRender: { additionalUrlParameters: { is_additional: true } },
      successHandler: (response: any) =>
        setGroups(response.data.results || response.data),
    });
  }, []);
  useEffect(() => {
    api.genericApiRequest({
      entity: "company",
      entityId: companyId,
      method: "get",
      successHandler: (response: any) => {
        setSelectedAssignments(
          response.data.additional_group_assignments || []
        );
        setInheritedAssignments(
          response.data.inherited_additional_group_assignments || []
        );
      },
    });
  }, [companyId]);

  const addGroup = (groupId: number) => {
    if (
      !selectedAssignments.some((assignment) => assignment.group_id === groupId)
    ) {
      setSelectedAssignments([
        ...selectedAssignments,
        { group_id: groupId, include_company_descendants: false },
      ]);
    }
  };
  const availableGroups = groups.filter(
    (group) =>
      !selectedAssignments.some(
        (assignment) => assignment.group_id === group.id
      ) &&
      !inheritedAssignments.some(
        (assignment) => assignment.group_id === group.id
      )
  );
  const displayedAssignments = [
    ...selectedAssignments.map((assignment) => ({
      ...assignment,
      inherited: false,
    })),
    ...inheritedAssignments
      .filter(
        (assignment) =>
          !selectedAssignments.some(
            (selected) => selected.group_id === assignment.group_id
          )
      )
      .map((assignment) => ({ ...assignment, inherited: true })),
  ];

  return (
    <Modal
      open={true}
      title={t("additional_groups_for_company_users")}
      onClose={onClose}
      disableSubmit={
        selectedAssignments.length === 0 && assignments.length === 0
      }
      onSubmit={() =>
        api.genericApiRequest({
          entity: "company",
          entityId: companyId,
          method: "put",
          submitData: {
            additional_group_assignments:
              toAdditionalGroupAssignments(selectedAssignments),
          },
          successHandler: onSaved,
        })
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="subtitle2">{t("add_additional_group")}</Typography>
        <Tooltip title={t("additional_groups_company_help")}>
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
      </Box>
      <Select
        fullWidth
        displayEmpty
        disabled={availableGroups.length === 0}
        value=""
        onChange={(event) => addGroup(Number(event.target.value))}
      >
        <MenuItem value="" disabled>
          {availableGroups.length === 0
            ? t("all_additional_groups_assigned")
            : t("select_additional_group")}
        </MenuItem>
        {availableGroups.map((group) => (
          <MenuItem key={group.id} value={group.id}>
            {group.name}
          </MenuItem>
        ))}
      </Select>
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        {t("assigned_additional_groups")}
      </Typography>
      {displayedAssignments.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t("no_additional_groups_assigned")}
        </Typography>
      )}
      <List dense>
        {displayedAssignments.map((assignment) => (
          <ListItem key={assignment.group_id} disableGutters>
            {!assignment.inherited && (
              <IconButton
                color="error"
                aria-label={t("delete")}
                onClick={() =>
                  setSelectedAssignments(
                    selectedAssignments.filter(
                      (item) => item.group_id !== assignment.group_id
                    )
                  )
                }
              >
                <DeleteIcon />
              </IconButton>
            )}
            <Box
              sx={{ display: "flex", flex: 1, alignItems: "center", gap: 0.5 }}
            >
              <ListItemText
                primary={
                  groups.find((group) => group.id === assignment.group_id)
                    ?.name || t("loading")
                }
                sx={{ m: 0, flex: "0 1 auto" }}
              />
              <Tooltip
                title={
                  assignment.inherited ? (
                    <>
                      <div>{t("additional_group_inherited_from_parent")}</div>
                      <div>
                        {t(
                          assignment.include_company_descendants
                            ? "additional_group_applies_to_company_and_descendants"
                            : "additional_group_applies_to_company_users"
                        )}
                      </div>
                    </>
                  ) : (
                    t(
                      assignment.include_company_descendants
                        ? "additional_group_applies_to_company_and_descendants"
                        : "additional_group_applies_to_company_users"
                    )
                  )
                }
              >
                {assignment.inherited ? (
                  <span style={{ color: "#8c1ec8" }}>ⓘ</span>
                ) : (
                  <InfoOutlinedIcon fontSize="small" color="action" />
                )}
              </Tooltip>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">
                {t("include_child_companies")}
              </Typography>
              <Switch
                checked={assignment.include_company_descendants}
                disabled={assignment.inherited}
                onChange={(event) =>
                  setSelectedAssignments(
                    selectedAssignments.map((item) =>
                      item.group_id === assignment.group_id
                        ? {
                            ...item,
                            include_company_descendants: event.target.checked,
                          }
                        : item
                    )
                  )
                }
                sx={{
                  "& .MuiSwitch-switchBase": {
                    color: "#8c1ec8",
                    "& + .MuiSwitch-track": { backgroundColor: "#8c1ec8" },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#ff6428",
                    "& + .MuiSwitch-track": { backgroundColor: "#ff6428" },
                  },
                }}
              />
            </Box>
          </ListItem>
        ))}
      </List>
    </Modal>
  );
}
